import { useId, useState } from 'react';
import { PiArrowRight, PiMagnifyingGlass } from 'react-icons/pi';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import SearchInput from 'src/modules/shared/components/molecules/inputs/search-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TemplatePickerProps } from '../../../types/templates/template-composer.types';
import {
  describeTemplate,
  templateDisplayName,
  templateLanguageLabel,
  templateCategoryLabel,
} from '../../../utils/template-composer.util';

/**
 * Searchable template library with explicit approval and format limitations.
 * @param props - Current feature state and consumer-owned callbacks.
 * @returns The corresponding composer surface.
 */
export const TemplatePicker = ({
  templates,
  onSelect,
}: TemplatePickerProps) => {
  const { t, language } = useI18n();
  const id = useId();
  const [search, setSearch] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [category, setCategory] = useState('');
  const [showUnavailable, setShowUnavailable] = useState(false);
  const languages = [
    ...new Set(templates.map((template) => template.language)),
  ];
  const categories = [
    ...new Set(templates.map((template) => template.category)),
  ];
  const definitions = templates.map((template) => ({
    template,
    definition: describeTemplate(template),
  }));
  const unavailableCount = definitions.filter(
    ({ definition }) => definition.unavailableReason
  ).length;
  const filtered = definitions.filter(({ template, definition }) => {
    const text =
      `${template.name} ${templateDisplayName(template.name)} ${definition.header} ${definition.body}`.toLocaleLowerCase(
        language
      );
    return (
      (!definition.unavailableReason || showUnavailable) &&
      (!selectedLanguage || template.language === selectedLanguage) &&
      (!category || template.category === category) &&
      text.includes(search.trim().toLocaleLowerCase(language))
    );
  });
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <SearchInput
        aria-label={t('whatsappTemplates.search')}
        placeholder={t('whatsappTemplates.search')}
        value={search}
        onChange={setSearch}
        className="shrink-0 !max-w-none"
        inputClassName="!text-base"
      />
      <div className="grid shrink-0 grid-cols-2 gap-3">
        <Select
          aria-label={t('whatsappTemplates.language')}
          value={selectedLanguage}
          onChange={(event) => setSelectedLanguage(event.target.value)}
          className="!text-base"
          options={[
            { value: '', content: t('whatsappTemplates.allLanguages') },
            ...languages.map((value) => ({
              value,
              content: templateLanguageLabel(value, language),
            })),
          ]}
        />
        <Select
          aria-label={t('whatsappTemplates.category')}
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          className="!text-base"
          options={[
            { value: '', content: t('whatsappTemplates.allCategories') },
            ...categories.map((value) => ({
              value,
              content: templateCategoryLabel(value, t),
            })),
          ]}
        />
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <Text size="tiny" color="secondary">
          {t('whatsappTemplates.results', { count: filtered.length })}
        </Text>
        {unavailableCount > 0 && (
          <label
            htmlFor={`${id}-unavailable`}
            className="flex cursor-pointer items-center gap-2 text-xs text-secondary-700"
          >
            <input
              id={`${id}-unavailable`}
              type="checkbox"
              checked={showUnavailable}
              onChange={(event) => setShowUnavailable(event.target.checked)}
              className="h-4 w-4 accent-neutral-900"
            />
            {t('whatsappTemplates.showUnavailable', {
              count: unavailableCount,
            })}
          </label>
        )}
      </div>
      <div
        role="region"
        aria-label={t('whatsappTemplates.choose')}
        className="flex min-h-32 max-h-[min(28rem,52dvh)] flex-1 flex-col gap-2 overflow-y-auto overscroll-contain rounded-lg"
        aria-live="polite"
      >
        {filtered.map(({ template, definition }) => (
          <Button
            key={template.id}
            variant="neutral"
            appearance="outline"
            onClick={() => onSelect(template.id)}
            className="!h-auto w-full shrink-0 !justify-between gap-3 !rounded-lg !p-3 text-left"
          >
            <span className="min-w-0 flex-1">
              <Text as="span" weight="medium" className="block truncate">
                {templateDisplayName(template.name)}
              </Text>
              <Text
                as="span"
                size="small"
                color="secondary"
                className="mt-1 block line-clamp-2 break-words sm:line-clamp-1"
                disableAutoLink
              >
                {definition.body || t('whatsappTemplates.noTextPreview')}
              </Text>
              <Text
                as="span"
                size="tiny"
                color="secondary"
                className="mt-1 block"
              >
                {templateLanguageLabel(template.language, language)} ·{' '}
                {templateCategoryLabel(template.category, t)} ·{' '}
                {template.status.toUpperCase() === 'APPROVED'
                  ? t('whatsappTemplates.approved')
                  : template.status}
              </Text>
              {definition.unavailableReason && (
                <Text
                  as="span"
                  size="tiny"
                  className="mt-2 block text-amber-800"
                >
                  {t(
                    `whatsappTemplates.unavailable.${definition.unavailableReason}`
                  )}
                </Text>
              )}
            </span>
            <PiArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </Button>
        ))}
        {filtered.length === 0 && (
          <EmptyState
            appearance="ghost"
            icon={<PiMagnifyingGlass className="h-5 w-5" aria-hidden />}
            title={t(
              templates.length
                ? 'whatsappTemplates.noResults'
                : 'whatsappTemplates.empty'
            )}
            description={t(
              templates.length
                ? 'whatsappTemplates.noResultsHelp'
                : 'whatsappTemplates.emptyHelp'
            )}
          />
        )}
      </div>
    </div>
  );
};
